import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ShowMeContextValue, ShowMeState, Scenario } from './types';
import { useTourProgress } from './hooks/useTourProgress';
import { getScenario } from './scenarios/registry';

const initialState: ShowMeState = {
  isActive: false,
  currentScenario: null,
  currentStepIndex: 0,
  stepHistory: [],
  decisions: {},
};

const ShowMeContext = createContext<ShowMeContextValue | null>(null);

/**
 * Compute the resolved step order based on decisions made.
 * Inserts branch steps after decision points.
 */
function computeResolvedStepOrder(
  scenario: Scenario,
  decisions: Record<string, string>
): string[] {
  const result: string[] = [];

  for (const stepId of scenario.defaultStepOrder) {
    result.push(stepId);

    // Check if this step has branches defined
    const branchOptions = scenario.branches?.[stepId];
    if (branchOptions) {
      const chosenOptionId = decisions[stepId];
      if (chosenOptionId) {
        const chosenOption = branchOptions.find((o) => o.id === chosenOptionId);
        if (chosenOption) {
          // Insert branch steps after decision
          result.push(...chosenOption.nextSteps);
        }
      }
    }
  }

  return result;
}

export function ShowMeProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [state, setState] = useState<ShowMeState>(initialState);
  const { markAsUnderstood } = useTourProgress();

  // Compute the resolved step order based on current decisions
  const resolvedStepOrder = useMemo(() => {
    if (!state.currentScenario) return [];
    return computeResolvedStepOrder(state.currentScenario, state.decisions);
  }, [state.currentScenario, state.decisions]);

  const startTour = useCallback((scenarioId: string) => {
    const scenario = getScenario(scenarioId);
    if (!scenario) {
      console.warn(`[ShowMe] Scenario not found: ${scenarioId}`);
      return;
    }
    setState({
      isActive: true,
      currentScenario: scenario,
      currentStepIndex: 0,
      stepHistory: [scenario.defaultStepOrder[0]],
      decisions: {},
    });
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => {
      if (!prev.currentScenario) return prev;
      const currentResolvedOrder = computeResolvedStepOrder(prev.currentScenario, prev.decisions);
      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex >= currentResolvedOrder.length) {
        // Tour complete
        return initialState;
      }
      const nextStepId = currentResolvedOrder[nextIndex];
      return {
        ...prev,
        currentStepIndex: nextIndex,
        stepHistory: [...prev.stepHistory, nextStepId],
      };
    });
  }, []);

  const previousStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStepIndex <= 0) return prev;
      return {
        ...prev,
        currentStepIndex: prev.currentStepIndex - 1,
        stepHistory: prev.stepHistory.slice(0, -1),
      };
    });
  }, []);

  const exitTour = useCallback(() => {
    setState(initialState);
  }, []);

  const makeDecision = useCallback((decisionId: string, optionId: string) => {
    setState((prev) => {
      // Update decisions and advance to next step
      const newDecisions = { ...prev.decisions, [decisionId]: optionId };
      const newResolvedOrder = computeResolvedStepOrder(prev.currentScenario!, newDecisions);
      const nextIndex = prev.currentStepIndex + 1;
      const nextStepId = newResolvedOrder[nextIndex];

      return {
        ...prev,
        decisions: newDecisions,
        currentStepIndex: nextIndex,
        stepHistory: [...prev.stepHistory, nextStepId],
      };
    });
  }, []);

  const markUnderstood = useCallback((scenarioId: string) => {
    markAsUnderstood(scenarioId);
    setState(initialState);
  }, [markAsUnderstood]);

  const value = useMemo<ShowMeContextValue>(() => ({
    ...state,
    resolvedStepOrder,
    startTour,
    nextStep,
    previousStep,
    exitTour,
    makeDecision,
    markUnderstood,
  }), [state, resolvedStepOrder, startTour, nextStep, previousStep, exitTour, makeDecision, markUnderstood]);

  return (
    <ShowMeContext.Provider value={value}>
      {children}
    </ShowMeContext.Provider>
  );
}

export function useShowMe(): ShowMeContextValue {
  const context = useContext(ShowMeContext);
  if (!context) {
    throw new Error('useShowMe must be used within ShowMeProvider');
  }
  return context;
}
